import { BackButton, DefaultButton } from 'common/presentation/components';
import {
	Box,
	Card,
	CardMedia,
	FormHelperText,
	Grid,
	IconButton,
	InputLabel,
	Stack,
	SvgIcon,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { zilaSlab } from 'assets/theme/theme';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { FileUpload } from 'primereact/fileupload';
import { AppEnv } from 'common/constants/app-env';
import LocalStorageWrapper, { LocalStorageKeys } from 'common/utils/storage-utils';
import { ApiResponse } from 'common/domain/entities/api-response';
import { useCatelog } from '../../provider/catelog.provider';
import { ProgressBar } from 'primereact/progressbar';
import { AddCataglogModel } from 'features/backoffice/data/dto/catelog-backoffice-model';
import AppLinks from 'assets/applinks.routes';
import useNotificationsContext from 'common/presentation/providers/notifications.provider';

const CatelogDetailPage = () => {
	const { t } = useTranslation('translations');
	const navigate = useNavigate();
	const theme = useTheme();
	const { toast } = useNotificationsContext();
	const { id } = useParams<{ id: string }>();
	const {
		getCatalogById,
		addEditCatalogState,
		catalog,
		setCatalog,
		createCatalog,
		catalogErrors,
		setCatalogErrors,
		updateCatalog,
	} = useCatelog();

	const [image, setImage] = useState<string>('');

	const chooseOptions = {
		icon: 'pi pi-fw pi-images',
		iconOnly: true,
		className: 'custom-choose-btn p-button-rounded p-button-outlined',
		style: {
			width: 200,
			height: 140,
			alignText: 'center',
		},
	};

	useEffect(() => {
		if (id && id !== '0') {
			getCatalogById(parseInt(id, 10));
		} else {
			setCatalog({
				name: '',
				fileUrl: '',
				imageUrl: '',
			} as AddCataglogModel);
			setCatalogErrors({
				name: '',
				fileUrl: '',
				imageUrl: '',
			} as AddCataglogModel);
		}
	}, [id]);

	useEffect(() => {
		if (catalog?.imageUrl) {
			setImage(catalog.imageUrl);
		}
	}, [catalog]);

	const handleSubmit = () => {
		const errors: AddCataglogModel = {
			name: '',
			fileUrl: '',
			imageUrl: '',
		} as AddCataglogModel;
		if (!catalog.name) {
			errors.name = t('required');
		}
		if (!catalog.fileUrl) {
			errors.fileUrl = t('required');
		} else if (!/^https?:\/\/\S+$/.test(catalog.fileUrl)) {
			errors.fileUrl = t('invalid_url');
		}
		if (!image) {
			errors.imageUrl = t('required');
		}
		setCatalogErrors(errors);
		if (errors.name || errors.fileUrl || errors.imageUrl) {
			return;
		}
		const imageFileName = catalog.imageUrl.split(/(\\|\/)/).pop()?.split(/(\\|\/|")/)[0] ?? '';
		if (id && id !== '0') {
			updateCatalog(parseInt(id, 10), {
				...catalog,
				imageUrl: imageFileName,
			} as AddCataglogModel).then((success) => {
				if (success) {
					toast.current?.show({
						severity: 'success',
						summary: 'Sucesso',
						detail: t('catalog_updated_successfully'),
					});
				} else {
					toast.current?.show({
						severity: 'error',
						summary: 'Erro',
						detail: addEditCatalogState.error,
					});
				}
			});
		} else {
			createCatalog({ ...catalog, imageUrl: imageFileName }).then((success) => {
				if (success) {
					toast.current?.show({
						severity: 'success',
						summary: 'Sucesso',
						detail: t('catalog_created_successfully'),
					});
					navigate(AppLinks.CATALOG);
				} else {
					toast.current?.show({
						severity: 'error',
						summary: 'Erro',
						detail: addEditCatalogState.error,
					});
				}
			});
		}
	};

	return (
		<Box>
			<Box>
				<BackButton onClick={() => navigate(-1)} />
			</Box>
			<Stack direction="row" mt={4}>
				<Typography fontSize={22} fontFamily={zilaSlab} fontWeight={600}>
					{id !== '0' ? t('edit_catalog') : t('create_catalog')}
				</Typography>
				<Box sx={{ flexGrow: 1 }} />
				<DefaultButton
					disabled={addEditCatalogState.loading}
					sx={{ borderRadius: 3, fontSize: 14 }}
					onClick={handleSubmit}
				>
					<SvgIcon component={AddIcon} fontSize="small" sx={{ mr: 1 }} />
					{t('save_changes')}
				</DefaultButton>
			</Stack>
			{(addEditCatalogState.loading) && (
				<ProgressBar mode="indeterminate" style={{ height: '6px' }} />
			)}
			<Grid container mt={4} spacing={2}>
				<Grid item md={4} xs={10}>
					<Card
						sx={{
							boxShadow: 'none',
							p: 4,
							gap: 2,
							display: 'flex',
						}}
					>
						<Stack direction="column" spacing={4} flex={1}>
							<div>
								<InputLabel shrink htmlFor="bootstrap-input">
									{t('catalog_name')}
								</InputLabel>
								<TextField
									fullWidth
									value={catalog?.name ?? ''}
									error={!!catalogErrors.name}
									helperText={catalogErrors.name}
									onChange={(e) => setCatalog((prev) => ({
										...prev, name: e.target.value,
									} as AddCataglogModel))}
								/>
							</div>
							<div>
								<InputLabel shrink htmlFor="bootstrap-input">
									{t('catalog_file_url')}
								</InputLabel>
								<TextField
									fullWidth
									value={catalog?.fileUrl ?? ''}
									error={!!catalogErrors.fileUrl}
									helperText={catalogErrors.fileUrl}
									onChange={(e) => setCatalog((prev) => ({
										...prev, fileUrl: e.target.value,
									} as AddCataglogModel))}
								/>
							</div>
							<div>
								<InputLabel shrink htmlFor="bootstrap-input">
									{t('catalog_image')}
								</InputLabel>
								<Card
									sx={{
										width: 200,
										boxShadow: ` 0px 0px 1.80666px ${theme.palette.secondary.main}`,
										borderTopLeftRadius: 0,
										borderTopRightRadius: 0,
									}}
								>
									<Stack sx={{ position: 'relative' }}>
										<IconButton
											sx={{ position: 'absolute', right: 0, top: 0 }}
											onClick={() => {
												setImage('');
												setCatalog((prev) => ({
													...prev, imageUrl: '',
												} as AddCataglogModel));
											}}
										>
											<DeleteIcon />
										</IconButton>
										{image && (
											<CardMedia
												component="img"
												height="140"
												// eslint-disable-next-line no-octal-escape
												image={image}
												alt="Uploaded Image"
												sx={{ objectFit: 'contain', height: '150px' }}
											/>
										)}
									</Stack>
								</Card>
								<FileUpload
									name="files"
									url={`${AppEnv.BASE_URL}backoffice/files/images`}
									mode="basic"
									multiple
									auto
									accept="image/*"
									maxFileSize={1000000}
									style={{ marginTop: 16 }}
									onBeforeSend={(e) => {
										const key = LocalStorageWrapper.get<string>(LocalStorageKeys.AUTH_TOKEN);
										if (key) {
											e.xhr.setRequestHeader('Authorization', `Bearer ${key}`);
										}
									}}
									onUpload={(e) => {
										const result = JSON.parse(
											e.xhr.response,
										) as ApiResponse<{ fileUrl: string }[]>;
										setImage(result.data[0].fileUrl);
										setCatalog((prev) => ({
											...prev, imageUrl: result.data[0].fileUrl,
										} as AddCataglogModel));
									}}
									chooseOptions={chooseOptions}
								/>
								{catalogErrors.imageUrl && (
									<FormHelperText error>{catalogErrors.imageUrl}</FormHelperText>
								)}
							</div>
						</Stack>
					</Card>
				</Grid>
			</Grid>
		</Box>
	);
};

export default CatelogDetailPage;
